import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { t, font, Button, Badge, Input } from "@realtyflows/ui";
import { getDocTemplate, getDeals, sendTemplate, previewTemplate } from "../api";
import SearchSelect from "../components/SearchSelect";
import useResponsive from "../hooks/useResponsive";

/* ═══════════════════════════════════════════════
   Field definitions — same as TemplateEdit
   ═══════════════════════════════════════════════ */

const ALL_FIELDS = [
  // Signature fields
  { raw: "\u00ABSIGN_1\u00BB", label: "Sign Here", signer: 1, type: "signature" },
  { raw: "\u00ABDATE_1\u00BB", label: "Date Signed", signer: 1, type: "signature" },
  { raw: "\u00ABNAME_1\u00BB", label: "Full Name", signer: 1, type: "signature" },
  { raw: "\u00ABINITIAL_1\u00BB", label: "Initials", signer: 1, type: "signature" },
  { raw: "\u00ABSIGN_2\u00BB", label: "Sign Here", signer: 2, type: "signature" },
  { raw: "\u00ABDATE_2\u00BB", label: "Date Signed", signer: 2, type: "signature" },
  { raw: "\u00ABNAME_2\u00BB", label: "Full Name", signer: 2, type: "signature" },
  { raw: "\u00ABINITIAL_2\u00BB", label: "Initials", signer: 2, type: "signature" },
  // CRM auto-fill fields
  { raw: "{{buyer_name}}", label: "Buyer Name", type: "crm" },
  { raw: "{{buyer_email}}", label: "Buyer Email", type: "crm" },
  { raw: "{{buyer_phone}}", label: "Buyer Phone", type: "crm" },
  { raw: "{{seller_name}}", label: "Seller Name", type: "crm" },
  { raw: "{{agent_name}}", label: "Agent Name", type: "crm" },
  { raw: "{{agent_email}}", label: "Agent Email", type: "crm" },
  { raw: "{{brokerage_name}}", label: "Brokerage", type: "crm" },
  { raw: "{{property_address}}", label: "Property Address", type: "crm" },
  { raw: "{{price_formatted}}", label: "Price", type: "crm" },
  { raw: "{{geographic_area}}", label: "Geographic Area", type: "crm" },
  { raw: "{{compensation_terms}}", label: "Compensation", type: "crm" },
  { raw: "{{agreement_start_date}}", label: "Start Date", type: "crm" },
  { raw: "{{agreement_end_date}}", label: "End Date", type: "crm" },
  { raw: "{{close_date}}", label: "Close Date", type: "crm" },
  { raw: "{{today}}", label: "Today's Date", type: "crm" },
  // Fillable fields
  { raw: "\u00ABTEXT_1\u00BB", label: "Text Input", signer: 1, type: "fillable" },
  { raw: "\u00ABADDR_1\u00BB", label: "Address", signer: 1, type: "fillable" },
  { raw: "\u00ABPHONE_1\u00BB", label: "Phone", signer: 1, type: "fillable" },
  { raw: "\u00ABCOMPANY_1\u00BB", label: "Company", signer: 1, type: "fillable" },
  { raw: "\u00ABTEXT_2\u00BB", label: "Text Input", signer: 2, type: "fillable" },
  { raw: "\u00ABADDR_2\u00BB", label: "Address", signer: 2, type: "fillable" },
  { raw: "\u00ABPHONE_2\u00BB", label: "Phone", signer: 2, type: "fillable" },
  { raw: "\u00ABCOMPANY_2\u00BB", label: "Company", signer: 2, type: "fillable" },
];

/**
 * Build preview HTML with styled chips for field tokens — same style as TemplateEdit preview.
 */
function buildChipPreviewHtml(body, templateName) {
  let html = body || "";

  for (const f of ALL_FIELDS) {
    const label = f.signer ? `${f.label} &middot; S${f.signer}` : f.label;
    const cls = f.type === "signature" ? "chip-sig" : f.type === "crm" ? "chip-crm" : "chip-fill";
    const chip = `<span class="${cls}">${label}</span>`;
    html = html.split(f.raw).join(chip);
    // Also handle HTML-encoded guillemets
    const encoded = f.raw.replace(/\u00AB/g, "&laquo;").replace(/\u00BB/g, "&raquo;");
    if (encoded !== f.raw) html = html.split(encoded).join(chip);
  }

  return `<!DOCTYPE html>
<html><head><style>
  body { font-family: 'Georgia', serif; color: #1a1a2e; margin: 0; padding: 40px 50px; font-size: 11pt; line-height: 1.6; }
  h1 { font-size: 16pt; margin-bottom: 4px; text-align: center; letter-spacing: 1px; }
  h2 { font-size: 12pt; margin: 24px 0 8px; border-bottom: 1px solid #ccc; padding-bottom: 4px; }
  .subtitle { text-align: center; font-size: 10pt; color: #555; margin-bottom: 24px; }
  .field { font-weight: bold; }
  .blank { display: inline-block; min-width: 200px; border-bottom: 1px solid #333; padding: 0 4px; }
  .sig-block { margin-top: 40px; display: flex; justify-content: space-between; }
  .sig-col { width: 45%; }
  .sig-line { border-bottom: 1px solid #333; margin-top: 40px; margin-bottom: 4px; }
  .sig-label { font-size: 9pt; color: #666; }
  ol { padding-left: 20px; }
  ol li { margin-bottom: 8px; }
  .footer { margin-top: 32px; padding-top: 12px; border-top: 1px solid #ccc; font-size: 8pt; color: #888; text-align: center; }
  .chip-sig { display:inline-flex; align-items:center; background:rgba(26,122,94,0.12); border:1.5px solid #1a7a5e; border-radius:4px; padding:1px 8px; font-size:11px; color:#1a7a5e; font-weight:600; white-space:nowrap; vertical-align:baseline; font-family:sans-serif; margin:0 1px; line-height:20px; }
  .chip-crm { display:inline-flex; align-items:center; background:rgba(59,130,246,0.12); border:1.5px solid #3b82f6; border-radius:4px; padding:1px 8px; font-size:11px; color:#3b82f6; font-weight:600; white-space:nowrap; vertical-align:baseline; font-family:sans-serif; margin:0 1px; line-height:20px; }
  .chip-fill { display:inline-flex; align-items:center; background:#f5f5f5; border:1.5px dashed #999; border-radius:4px; padding:1px 8px; font-size:11px; color:#666; font-weight:600; white-space:nowrap; vertical-align:baseline; font-family:sans-serif; margin:0 1px; line-height:20px; }
</style></head><body>
${html}
<div class="footer">Generated by RealtyFlows LLC</div>
</body></html>`;
}

export default function TemplatePreview({ user }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const responsive = useResponsive();
  const isMobile = responsive.width < 640;
  const isCompact = responsive.isCompact;

  const [template, setTemplate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [sendModal, setSendModal] = useState(false);
  const [deals, setDeals] = useState([]);
  const [sendForm, setSendForm] = useState({ deal_id: "", signerName: "", signerEmail: "", signer2Name: "", signer2Email: "" });
  const [sending, setSending] = useState(false);
  const [toast, setToast] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);

  useEffect(() => {
    getDocTemplate(id)
      .then(setTemplate)
      .catch(() => navigate("/contracts/templates"))
      .finally(() => setLoading(false));
  }, [id, navigate]);

  // Build preview: use template body with chips if available, otherwise fetch from API and apply chips
  const buildPreview = useCallback(async (tmpl) => {
    let body;
    if (tmpl.body) {
      body = tmpl.body;
    } else {
      // Fetch from API and extract body content
      try {
        const html = await previewTemplate(tmpl.id);
        const match = html.match(/<body[^>]*>([\s\S]*)<\/body>/i);
        body = match ? match[1].replace(/<div class="footer">[\s\S]*<\/div>\s*$/, "") : "";
      } catch {
        body = `<h1>${tmpl.name}</h1><p>No content available.</p>`;
      }
    }
    const previewHtml = buildChipPreviewHtml(body, tmpl.name);
    const blob = new Blob([previewHtml], { type: "text/html" });
    if (previewUrl) URL.revokeObjectURL(previewUrl);
    setPreviewUrl(URL.createObjectURL(blob));
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (template) {
      document.title = `${template.name} — RealtyFlows`;
      buildPreview(template);
    }
    return () => { document.title = "RealtyFlows"; };
  }, [template, buildPreview]);

  useEffect(() => {
    if (!toast) return;
    const timer = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(timer);
  }, [toast]);

  const openSendModal = async () => {
    try {
      const d = await getDeals();
      setDeals(template.pipeline_type ? d.filter((dl) => (dl.pipeline_type || "buyer") === template.pipeline_type) : d);
    } catch {}
    setSendModal(true);
  };

  const handleSend = async () => {
    if (!sendForm.signerEmail) {
      setToast({ message: "Add a signer email", variant: "error" });
      return;
    }
    if (!template.agent_is_signer_2 && !sendForm.signer2Email) {
      setToast({ message: "Add a second signer email", variant: "error" });
      return;
    }
    setSending(true);
    try {
      const isBuyer = (template.pipeline_type || "buyer") === "buyer";
      const signers = [{
        name: sendForm.signerName, email: sendForm.signerEmail,
        role: template.agent_is_signer_2 ? "Client" : (isBuyer ? "Buyer" : "Seller"),
      }];
      if (!template.agent_is_signer_2) {
        signers.push({
          name: sendForm.signer2Name, email: sendForm.signer2Email,
          role: isBuyer ? "Seller" : "Buyer",
        });
      }
      const payload = { signers };
      if (sendForm.deal_id) payload.deal_id = parseInt(sendForm.deal_id);
      await sendTemplate(template.id, payload);
      setToast({ message: `${template.name} sent for signing`, variant: "success" });
      setSendModal(false);
      setSendForm({ deal_id: "", signerName: "", signerEmail: "", signer2Name: "", signer2Email: "" });
    } catch (err) {
      setToast({ message: err.message || "Failed to send", variant: "error" });
    } finally {
      setSending(false);
    }
  };

  if (loading) return <div style={{ padding: 40, textAlign: "center", color: t.textMuted, fontFamily: font.body }}>Loading...</div>;
  if (!template) return null;

  const pipelineLabel = template.pipeline_type === "seller" ? "Seller" : "Buyer";

  return (
    <div style={{ animation: "fadeSlide 0.3s ease", maxWidth: 800, margin: "0 auto" }}>
      {/* Toast */}
      {toast && (
        <div style={{
          position: "fixed", top: 20, right: 20, zIndex: 999,
          background: toast.variant === "error" ? t.coral : t.emerald,
          color: "#fff", padding: isMobile ? "8px 14px" : "10px 20px", borderRadius: 8,
          fontSize: isMobile ? 11 : 12, fontWeight: 600, fontFamily: font.body,
          boxShadow: "0 4px 16px rgba(0,0,0,0.15)",
          maxWidth: isMobile ? "calc(100vw - 40px)" : "auto",
        }}>
          {toast.message}
        </div>
      )}

      {/* Send Modal */}
      {sendModal && (
        <div style={{
          position: "fixed", inset: 0, background: "rgba(0,0,0,0.4)", zIndex: 998,
          display: "flex", alignItems: "center", justifyContent: "center",
          padding: isMobile ? 16 : 0,
        }} onClick={() => setSendModal(false)}>
          <div style={{
            background: t.surface, borderRadius: 12,
            padding: isMobile ? 16 : 20,
            width: isMobile ? "calc(100vw - 32px)" : 380,
            maxWidth: 420,
            maxHeight: "80vh", overflowY: "auto",
            border: `1px solid ${t.emerald}30`, boxShadow: "0 8px 32px rgba(0,0,0,0.2)",
          }} onClick={(e) => e.stopPropagation()}>
            <div style={{ fontSize: isMobile ? 15 : 16, fontWeight: 700, color: t.textPrimary, marginBottom: 4, fontFamily: font.display }}>
              Send for Signing
            </div>
            <div style={{ fontSize: isMobile ? 11 : 12, color: t.textSecondary, marginBottom: 6, fontFamily: font.body }}>
              {template.name}
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: isMobile ? 12 : 14 }}>
              <div>
                <SearchSelect
                  label="Deal"
                  value={sendForm.deal_id}
                  onChange={(val) => {
                    const deal = val ? deals.find((d) => d.id === parseInt(val)) : null;
                    setSendForm((prev) => ({
                      ...prev,
                      deal_id: val,
                      signerName: deal ? prev.signerName || deal.client_name || "" : prev.signerName,
                      signerEmail: deal ? prev.signerEmail || deal.client_email || "" : prev.signerEmail,
                    }));
                  }}
                  options={deals.map((d) => ({
                    value: String(d.id),
                    label: d.client_name || "No contact",
                    subtitle: d.property_address || "No address",
                  }))}
                  emptyLabel="-- No deal (auto-create) --"
                  placeholder="Select a deal..."
                />
                {!sendForm.deal_id && (
                  <div style={{ fontSize: 10, color: t.textMuted, fontFamily: font.body, marginTop: 4 }}>
                    A new deal will be created and moved to the "{template.stage || "Client"}" stage
                  </div>
                )}
              </div>

              {/* Signer 1 */}
              <div style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, fontFamily: font.body, marginBottom: -8 }}>
                {(template.pipeline_type || "buyer") === "buyer" ? "Signer 1 — Buyer" : "Signer 1 — Seller"}
              </div>
              <Input label="Name" value={sendForm.signerName} onChange={(e) => setSendForm((prev) => ({ ...prev, signerName: e.target.value }))} placeholder="John Smith" />
              <Input label="Email" value={sendForm.signerEmail} onChange={(e) => setSendForm((prev) => ({ ...prev, signerEmail: e.target.value }))} placeholder="john@example.com" />

              {/* Signer 2 */}
              <div style={{ borderTop: `1px solid ${t.border}`, paddingTop: 14, marginTop: 2 }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: t.textSecondary, fontFamily: font.body }}>
                  {template.agent_is_signer_2
                    ? "Signer 2 — Agent"
                    : ((template.pipeline_type || "buyer") === "buyer" ? "Signer 2 — Seller" : "Signer 2 — Buyer")}
                </div>
              </div>
              {template.agent_is_signer_2 ? (
                <div style={{
                  padding: isMobile ? "10px 12px" : "12px 14px", background: t.surfaceWarm, border: `1px solid ${t.border}`,
                  borderRadius: 8, display: "flex", flexDirection: "column", gap: 4,
                }}>
                  <div style={{ fontSize: isMobile ? 12 : 13, fontWeight: 600, color: t.textPrimary, fontFamily: font.body }}>
                    {user?.name || "Agent"}
                  </div>
                  <div style={{ fontSize: isMobile ? 11 : 12, color: t.textMuted, fontFamily: font.mono }}>
                    {user?.email || ""}
                  </div>
                  <div style={{ fontSize: 10, color: t.textSecondary, fontFamily: font.body, marginTop: 2 }}>
                    You will sign as Signer 2 after sending
                  </div>
                </div>
              ) : (
                <>
                  <Input label="Name" value={sendForm.signer2Name} onChange={(e) => setSendForm((prev) => ({ ...prev, signer2Name: e.target.value }))} placeholder="Jane Doe" />
                  <Input label="Email" value={sendForm.signer2Email} onChange={(e) => setSendForm((prev) => ({ ...prev, signer2Email: e.target.value }))} placeholder="jane@example.com" />
                </>
              )}
            </div>
            <div style={{
              display: "flex", gap: 10,
              justifyContent: isMobile ? "stretch" : "flex-end",
              marginTop: isMobile ? 16 : 20,
              flexDirection: isMobile ? "column-reverse" : "row",
            }}>
              <Button variant="outline" size="sm" onClick={() => setSendModal(false)}>Cancel</Button>
              <Button variant="primary" size="sm" onClick={handleSend} disabled={sending}>
                {sending ? "Sending..." : "Send for Signing"}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Top bar */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "space-between",
        marginBottom: isMobile ? 16 : 24,
        flexWrap: isMobile ? "wrap" : "nowrap",
        gap: isMobile ? 8 : 0,
      }}>
        <button onClick={() => navigate("/contracts/templates")}
          style={{
            display: "flex", alignItems: "center", gap: 6, background: "none", border: "none",
            cursor: "pointer", color: t.textSecondary, fontSize: isMobile ? 12 : 13, fontFamily: font.body, padding: 0,
          }}
          onMouseEnter={(e) => (e.currentTarget.style.color = t.emerald)}
          onMouseLeave={(e) => (e.currentTarget.style.color = t.textSecondary)}
        >
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <polyline points="15 18 9 12 15 6" />
          </svg>
          Back to Templates
        </button>
        <div style={{
          display: "flex", gap: 8,
          width: isMobile ? "100%" : "auto",
        }}>
          <Button variant="outline" size="sm" onClick={() => navigate(`/contracts/templates/${id}/edit`)}
            style={isMobile ? { flex: 1 } : undefined}>
            Edit
          </Button>
          <Button variant="primary" size="sm" onClick={openSendModal}
            style={isMobile ? { flex: 1 } : undefined}>
            Send for Signing
          </Button>
        </div>
      </div>

      {/* Template Card */}
      <div style={{ background: t.surface, border: `1px solid ${t.border}`, borderRadius: 12, overflow: "hidden" }}>
        {/* Header — document name first */}
        <div style={{ padding: isMobile ? "16px 14px" : "24px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{ display: "flex", alignItems: isMobile ? "flex-start" : "center", gap: isMobile ? 10 : 14 }}>
            <div style={{
              width: isMobile ? 40 : 48, height: isMobile ? 40 : 48, borderRadius: 10, background: `${t.emerald}12`,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}>
              <svg width={isMobile ? 20 : 24} height={isMobile ? 20 : 24} viewBox="0 0 24 24" fill="none" stroke={t.emerald} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
                <polyline points="14 2 14 8 20 8" />
                <line x1="16" y1="13" x2="8" y2="13" />
                <line x1="16" y1="17" x2="8" y2="17" />
              </svg>
            </div>
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: isMobile ? 16 : 20, fontWeight: 700, color: t.textPrimary, fontFamily: font.display, wordBreak: "break-word" }}>
                {template.name}
              </div>
              {template.description && (
                <div style={{ fontSize: isMobile ? 12 : 13, color: t.textSecondary, fontFamily: font.body, marginTop: 2 }}>
                  {template.description}
                </div>
              )}
              <div style={{ display: "flex", gap: 6, marginTop: 8, flexWrap: "wrap" }}>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: `${t.sky}15`, color: t.sky, fontFamily: font.mono }}>
                  {(template.trigger_type || "native").toUpperCase()} E-SIGN
                </span>
                <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: `${t.amber}15`, color: t.amber, fontFamily: font.mono }}>
                  {pipelineLabel.toUpperCase()} PIPELINE
                </span>
                {template.stage && (
                  <span style={{ fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 4, background: t.surfaceWarm, color: t.textMuted, fontFamily: font.mono }}>
                    STAGE: {template.stage.toUpperCase()}
                  </span>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Document Preview — with styled chips like edit/preview mode */}
        <div style={{ padding: isMobile ? "16px 14px" : "24px 28px", borderBottom: `1px solid ${t.border}` }}>
          <div style={{
            display: "flex", alignItems: isMobile ? "flex-start" : "center",
            justifyContent: "space-between", marginBottom: 12,
            flexDirection: isMobile ? "column" : "row",
            gap: isMobile ? 4 : 0,
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, fontFamily: font.mono, letterSpacing: 1 }}>DOCUMENT PREVIEW</div>
            <div style={{ fontSize: isMobile ? 10 : 11, color: t.textMuted, fontFamily: font.body }}>Fields shown as chips — filled with deal data when sent</div>
          </div>
          <div style={{
            border: `1.5px solid ${t.border}`, borderRadius: 10, overflow: "hidden",
            background: "#fff", position: "relative",
          }}>
            {previewUrl ? (
              <iframe
                src={previewUrl}
                title="Document Preview"
                style={{ width: "100%", height: isMobile ? 350 : 700, border: "none", background: "#fff" }}
              />
            ) : (
              <div style={{ padding: isMobile ? 30 : 60, textAlign: "center", color: t.textMuted, fontSize: 13, fontFamily: font.body }}>
                Loading document preview...
              </div>
            )}
            {/* Field legend */}
            <div style={{
              padding: isMobile ? "10px 12px" : "12px 20px", background: t.surfaceWarm,
              borderTop: `1px solid ${t.border}`,
              display: "flex", alignItems: "center", gap: isMobile ? 10 : 16, flexWrap: "wrap",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{
                  display: "inline-block", width: 14, height: 14, borderRadius: 3,
                  background: "rgba(26,122,94,0.12)", border: "1.5px solid #1a7a5e",
                }} />
                <span style={{ fontSize: isMobile ? 10 : 11, color: t.textSecondary, fontFamily: font.body }}>Signature Fields</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{
                  display: "inline-block", width: 14, height: 14, borderRadius: 3,
                  background: "rgba(59,130,246,0.12)", border: "1.5px solid #3b82f6",
                }} />
                <span style={{ fontSize: isMobile ? 10 : 11, color: t.textSecondary, fontFamily: font.body }}>Auto-filled from CRM</span>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                <span style={{
                  display: "inline-block", width: 14, height: 14, borderRadius: 3,
                  background: "#f5f5f5", border: "1.5px dashed #999",
                }} />
                <span style={{ fontSize: isMobile ? 10 : 11, color: t.textSecondary, fontFamily: font.body }}>Signer Fills In</span>
              </div>
            </div>
          </div>
        </div>

        {/* Details Grid */}
        <div style={{ padding: isMobile ? "16px 14px" : "24px 28px" }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: t.textMuted, marginBottom: 12, fontFamily: font.mono, letterSpacing: 1 }}>DETAILS</div>
          <div style={{
            display: "grid",
            gridTemplateColumns: isMobile ? "1fr 1fr" : isCompact ? "1fr 1fr" : "1fr 1fr 1fr 1fr",
            gap: isMobile ? 10 : 14,
          }}>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: isMobile ? 10 : 14 }}>
              <div style={{ fontSize: isMobile ? 9 : 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 4 }}>PIPELINE</div>
              <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>{pipelineLabel}</div>
            </div>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: isMobile ? 10 : 14 }}>
              <div style={{ fontSize: isMobile ? 9 : 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 4 }}>STAGE</div>
              <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>{template.stage || "Any"}</div>
            </div>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: isMobile ? 10 : 14 }}>
              <div style={{ fontSize: isMobile ? 9 : 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 4 }}>E-SIGN TYPE</div>
              <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>{template.trigger_type === "native" ? "Native" : "Manual"}</div>
            </div>
            <div style={{ background: t.surfaceWarm, borderRadius: 8, padding: isMobile ? 10 : 14 }}>
              <div style={{ fontSize: isMobile ? 9 : 10, color: t.textMuted, fontFamily: font.mono, marginBottom: 4 }}>CREATED</div>
              <div style={{ fontSize: isMobile ? 13 : 14, fontWeight: 700, color: t.textPrimary, fontFamily: font.body }}>
                {new Date(template.created_at).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
